import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import Modal from '../common/Modal';
import MiniLoader from '../common/MiniLoader';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';

// Two action tiles — Register / Activate.
// Register opens its own modal (self-contained).
// Activate calls onActivateClick — Home renders the shared ActivateForModal.
const SponsorActions = ({ userId, onSuccess, onActivateClick }) => {
  const [regOpen, setRegOpen] = useState(false);

  return (
    <>
      <div className="action-grid">
        <button className="action-tile" onClick={() => setRegOpen(true)}>
          <div className="action-icon" style={{ background: 'linear-gradient(135deg,#7C3AED,#EC4899)' }}>➕</div>
          <div className="action-title">Register User</div>
          <div className="action-desc">Add a new member to your team</div>
        </button>

        <button className="action-tile" onClick={() => onActivateClick('')}>
          <div className="action-icon" style={{ background: 'linear-gradient(135deg,#22D3EE,#7C3AED)' }}>⚡</div>
          <div className="action-title">Activate Package</div>
          <div className="action-desc">Pay to activate for another user</div>
        </button>
      </div>

      {regOpen && (
        <RegisterUserModal
          onClose={() => setRegOpen(false)}
          defaultReferrer={userId}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
};

const RegisterUserModal = ({ onClose, defaultReferrer, onSuccess }) => {
  const { logic, storage } = useContracts();
  const notify = useNotify();
  const [wallet, setWallet] = useState('');
  const [refId, setRefId] = useState(String(defaultReferrer || ''));
  const [busy, setBusy] = useState(false);

  // Live upline lookup — shows who the referrer is
  const [upline, setUpline] = useState(null); // { id, topPkg } | 'notfound' | null
  const [uplineLoading, setUplineLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const lookup = async () => {
      const num = parseInt(refId, 10);
      if (!num || num < 1) { setUpline(null); return; }
      setUplineLoading(true);
      try {
        const u = await storage.users(num);
        if (cancelled) return;
        const id = u[1].toNumber ? u[1].toNumber() : Number(u[1]);
        if (id === 0) {
          setUpline('notfound');
        } else {
          setUpline({
            id,
            topPkg: u[8].toNumber ? u[8].toNumber() : Number(u[8])
          });
        }
      } catch (_) {
        if (!cancelled) setUpline('notfound');
      } finally {
        if (!cancelled) setUplineLoading(false);
      }
    };
    const t = setTimeout(lookup, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [refId, storage]);

  const submit = async () => {
    if (!ethers.utils.isAddress(wallet)) { notify.error('Invalid wallet address'); return; }
    const ref = parseInt(refId, 10);
    if (!ref) { notify.error('Invalid referrer ID'); return; }
    if (upline === 'notfound') { notify.error('Referrer not found'); return; }

    setBusy(true);
    const l = notify.loading('Registering user...');
    try {
      const tx = await logic.registerFor(wallet, ref);
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success('User registered successfully');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={busy ? undefined : onClose} title="Register New User">
      <div className="input-group">
        <label className="input-label">Wallet Address</label>
        <input className="input font-mono" placeholder="0x..." value={wallet} onChange={(e) => setWallet(e.target.value)} />
      </div>
      <div className="input-group">
        <label className="input-label">Referrer ID</label>
        <input className="input" type="number" value={refId} onChange={(e) => setRefId(e.target.value)} />

        {uplineLoading && (
          <span className="input-hint"><MiniLoader /> Looking up referrer...</span>
        )}
        {!uplineLoading && upline && upline !== 'notfound' && (
          <div className="upline-preview">
            <div className="upline-avatar">
              {String(upline.id).charAt(0)}
            </div>
            <div className="upline-info">
              <div className="upline-name">User #{upline.id}</div>
              <div className="upline-meta">ID {upline.id} · Rank P{upline.topPkg || 0}</div>
            </div>
            <div className="upline-badge">✓ Verified</div>
          </div>
        )}
        {!uplineLoading && upline === 'notfound' && (
          <span className="input-error">✕ Referrer ID not found</span>
        )}
        {!uplineLoading && !upline && (
          <span className="input-hint">Default: your own ID</span>
        )}
      </div>
      <div className="flex gap-sm mt-md">
        <button className="btn btn-secondary flex-1" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary flex-1" onClick={submit} disabled={busy || upline === 'notfound'}>
          {busy ? <><MiniLoader /> Registering</> : 'Register'}
        </button>
      </div>
    </Modal>
  );
};

export default SponsorActions;
